<script setup lang="ts">
import { HInput, type ValidationRule } from '@headless/atoms'

const props = withDefaults(defineProps<{
  modelValue: string
  label?: string
  placeholder?: string
  type?: 'text' | 'email' | 'password' | 'tel' | 'number' | 'date'
  rules?: ValidationRule[]
  disabled?: boolean
  required?: boolean
}>(), {
  type: 'text',
  rules: () => [],
  disabled: false,
  required: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()
</script>

<template>
  <HInput
    :modelValue="modelValue"
    :rules="rules"
    @update:modelValue="emit('update:modelValue', $event)"
    v-slot="{ value, error, focused, onInput, onFocus, onBlur }"
  >
    <div :class="['input-field', { 'input-field--error': error, 'input-field--focused': focused }]">
      <label v-if="label" class="input-field__label">
        {{ label }}
        <span v-if="required" class="input-field__required">*</span>
      </label>
      <input
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        class="input-field__input"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <span v-if="error" class="input-field__error">{{ error }}</span>
    </div>
  </HInput>
</template>

<style scoped>
.input-field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.input-field__label {
  font-size: var(--typography-body-small-size);
  font-weight: 500;
  color: var(--colors-text-secondary);
}

.input-field__required {
  color: var(--colors-error);
}

.input-field__input {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--typography-body-default-size);
  font-family: var(--typography-font-family);
  color: var(--colors-text-primary);
  background-color: var(--colors-background-base);
  border: 1px solid var(--colors-border-default);
  border-radius: var(--radius-md);
  transition: all var(--transition-fast);
}

.input-field__input:focus {
  outline: none;
  border-color: var(--colors-border-focus);
  box-shadow: 0 0 0 3px var(--colors-primary-light);
}

.input-field__input:disabled {
  background-color: var(--colors-background-surface);
  color: var(--colors-text-disabled);
  cursor: not-allowed;
}

.input-field__input::placeholder {
  color: var(--colors-text-disabled);
}

.input-field--error .input-field__input {
  border-color: var(--colors-border-error);
}

.input-field__error {
  font-size: var(--typography-body-small-size);
  color: var(--colors-error);
}
</style>
